(() => {
  const SELECTORS = {
    section: "[data-pd-navigator-section]",
    tabs: "[data-pd-tabs]",
    stockGrid: "[data-stock-grid]",
    designer: "[data-pd-designer]",
    heroGallery: "[data-hero-gallery]"
  };

  const DEFAULT_COLOR = "#ffffff";
  const PREVIEW_MESH_OPACITY = 0.85;
  const DEFAULT_DESIGN_NAME_KEY = "default_design_name";
  const DEFAULT_VIEW_ZOOM = 72;
  const COMPACT_VIEW_ZOOM = 88;
  const COMPACT_VIEW_MAX_WIDTH = 420;
  const MIN_VIEW_ZOOM = 12;
  const MAX_VIEW_ZOOM = 110;
  const SHARE_PARAM = "pd_navigator_design";
  const EUROPEAN_COUNTRY_CODES = new Set([
    "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
    "IS", "LI", "NO", "CH", "GB", "UK", "AD", "MC", "SM", "VA"
  ]);

  function cleanHex(hex) {
    const raw = String(hex || "").replace("#", "").trim().toLowerCase();
    const expanded = /^[0-9a-f]{3}$/i.test(raw)
      ? raw.split("").map((char) => `${char}${char}`).join("")
      : raw;
    return `#${expanded}`;
  }
  const compactHex = (hex) => cleanHex(hex).replace("#", "");
  const getJson = async (url, t) => {
    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) throw new Error(t("could_not_load", { url }));
    return response.json();
  };

  const escapeHTML = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[char]);

  function translationsFor(section) {
    const node = section.querySelector("[data-pd-navigator-translations]");
    try { return node ? JSON.parse(node.textContent) : {}; } catch (error) { console.error(error); return {}; }
  }

  function translate(translations, key, values = {}) {
    const source = translations[key] || key;
    return String(source).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, name) => String(values[name] ?? ""));
  }

  function encodeBase64Url(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (let index = 0; index < bytes.length; index += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
    }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  }

  function decodeBase64Url(value) {
    const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`;
    const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new TextDecoder().decode(bytes);
  }

  function initTabs(root) {
    root.querySelectorAll(SELECTORS.tabs).forEach((tabs) => {
      const buttons = [...tabs.querySelectorAll("[data-tab]")];
      const panels = [...tabs.querySelectorAll("[data-panel]")];
      buttons.forEach((button) => {
        button.addEventListener("click", () => {
          buttons.forEach((item) => {
            item.classList.toggle("is-active", item === button);
            item.setAttribute("aria-selected", item === button ? "true" : "false");
          });
          panels.forEach((panel) => {
            panel.classList.toggle("is-active", panel.dataset.panel === button.dataset.tab);
          });
        });
      });
    });
  }

  function initHeroGallery(root) {
    root.querySelectorAll(SELECTORS.heroGallery).forEach((gallery) => {
      const slides = [...gallery.querySelectorAll("[data-gallery-slide]")];
      const thumbs = [...gallery.querySelectorAll("[data-gallery-thumb]")];
      const previous = gallery.querySelector("[data-gallery-prev]");
      const next = gallery.querySelector("[data-gallery-next]");
      if (slides.length <= 1) return;
      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));

      function setActive(index) {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => slide.classList.toggle("is-active", slideIndex === activeIndex));
        thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === activeIndex;
          thumb.classList.toggle("is-active", isActive);
          thumb.setAttribute("aria-current", isActive ? "true" : "false");
        });
      }

      thumbs.forEach((thumb, index) => thumb.addEventListener("click", () => setActive(index)));
      previous?.addEventListener("click", () => setActive(activeIndex - 1));
      next?.addEventListener("click", () => setActive(activeIndex + 1));
      setActive(activeIndex);
    });
  }

  function initStock(section, stockData) {
    const translations = translationsFor(section);
    const t = (key, values) => translate(translations, key, values);
    const grid = section.querySelector(SELECTORS.stockGrid);
    const template = section.querySelector("[data-stock-card-template]");
    const filterButtons = [...section.querySelectorAll("[data-stock-filter]")];
    const sizeOptions = section.querySelector("[data-stock-size-options]");
    const count = section.querySelector("[data-stock-count]");
    const featured = section.querySelector("[data-featured-stock]");
    const variantId = section.dataset.stockVariantId;
    const stockPrice = section.dataset.stockPrice;
    const moneyCurrency = section.dataset.moneyCurrency || "EUR";
    const moneyLocale = section.dataset.moneyLocale || document.documentElement.lang || "en";
    const customerCountry = (section.dataset.customerCountry || "").toUpperCase();
    const marketHandle = section.dataset.marketHandle || "";
    const marketName = section.dataset.marketName || "";
    const enableCartPosts = section.dataset.enableCartPosts === "true";
    const stockValidationUrl = section.dataset.stockValidationUrl;
    const sizeVariantMap = (() => {
      try { return JSON.parse(section.dataset.sizeVariantMap || "{}"); } catch { return {}; }
    })();
    let region = "all";
    let size = "all";

    function variantInfoForSize(stockSize) {
      return sizeVariantMap[String(stockSize)] || sizeVariantMap[`${stockSize} sq ft`] || null;
    }

    function isClearanceStock(item) {
      return /clearance/i.test(item.category || "");
    }

    function clearanceVariantKey(item) {
      return `${item.size} Clearance`;
    }

    function variantInfoForStock(item) {
      if (isClearanceStock(item)) {
        return sizeVariantMap[clearanceVariantKey(item)] || null;
      }
      return variantInfoForSize(item.size);
    }

    function needsManualStockReview(item) {
      return /used|resale/i.test(item.category || "") || Boolean(item.price_display);
    }

    function isEuropeanMarket() {
      const marketLooksEuropean = /\b(eu|europe|european)\b/i.test(`${marketHandle} ${marketName}`);
      const countryLooksEuropean = customerCountry ? EUROPEAN_COUNTRY_CODES.has(customerCountry) : false;
      return moneyCurrency === "EUR" && (marketLooksEuropean || countryLooksEuropean || !customerCountry);
    }

    function money(cents, fallback = "") {
      if (!Number.isFinite(cents) || cents <= 0) return fallback;
      try {
        return new Intl.NumberFormat(moneyLocale, { style: "currency", currency: moneyCurrency }).format(cents / 100);
      } catch {
        return fallback || `${moneyCurrency} ${(cents / 100).toFixed(2)}`;
      }
    }

    async function hydrateVariantMapFromProductJson() {
      try {
        const response = await fetch(`${window.location.pathname}.js`, { headers: { Accept: "application/json" } });
        if (!response.ok) return;
        const productJson = await response.json();
        (productJson.variants || []).forEach((variant) => {
          const key = String(variant.option1 || variant.title || "");
          if (!key) return;
          const priceCents = Number(variant.price || 0);
          const compareAtCents = Number(variant.compare_at_price || 0);
          sizeVariantMap[key] = {
            id: variant.id,
            title: variant.title || key,
            price: money(priceCents),
            price_cents: priceCents,
            compare_at_price: compareAtCents > 0 ? money(compareAtCents) : null,
            compare_at_price_cents: compareAtCents || 0
          };
        });
      } catch (error) {
        console.warn("Navigator variant map hydration failed", error);
      }
    }

    function usedPriceTextFor(item) {
      if (isEuropeanMarket()) {
        const eurCents = Number(item.used_price_eur_cents || 0);
        if (Number.isFinite(eurCents) && eurCents > 0) return money(eurCents);
        if (item.price_currency === "EUR" && item.price_display) return item.price_display;
        if (item.used_price_eur_display) return item.used_price_eur_display;
      }
      return item.source_price_display || item.price_display || "";
    }

    function usedPriceLabelFor(item) {
      const display = usedPriceTextFor(item);
      return item.price_currency === "EUR" || /€/.test(display) ? t("used_price") : t("source_price");
    }

    if (!grid || !template || !stockData?.items) return;

    const sizes = [...new Set(stockData.items.map((item) => item.size).filter(Boolean))]
      .sort((a, b) => Number(a) - Number(b));
    sizes.forEach((stockSize) => {
      const button = document.createElement("button");
      button.type = "button";
      button.dataset.stockSize = stockSize;
      button.textContent = `${stockSize} sq ft`;
      sizeOptions?.append(button);
    });
    const sizeButtons = () => [...section.querySelectorAll("[data-stock-size]")];

    function filteredItems() {
      return stockData.items.filter((item) => {
        const matchesRegion = region === "all" || item.locations.includes(region);
        const matchesSize = size === "all" || item.size === size;
        return matchesRegion && matchesSize;
      });
    }

    function detailsFor(item) {
      const rows = [
        [t("condition_notes"), item.details?.condition_notes]
      ];
      if (isClearanceStock(item)) {
        const baseVariant = variantInfoForSize(item.size);
        if (baseVariant?.price) rows.push([t("discount"), t("discount_was", { price: baseVariant.price })]);
      }
      return rows.filter(([, value]) => value);
    }

    function stockReferenceFor(item) {
      return item.details?.name || String(item.stock_id);
    }

    function stockCountText(total) {
      return t(total === 1 ? "stock_count_one" : "stock_count_other", { count: total });
    }

    function renderFeatured() {
      const item = stockData.items[0];
      if (!featured || !item) return;
      featured.innerHTML = `
        <img src="${escapeHTML(item.snapshot_url)}" alt="${escapeHTML(t("stock_canopy_alt", { name: item.name }))}">
        <div>
          <strong>${escapeHTML(item.name)}</strong>
          <p>${escapeHTML(t("stock_meta", { locations: item.locations.join(" + "), line_set: item.details?.line_set || t("line_set_fallback") }))}</p>
        </div>
      `;
    }

    async function validateStockItem(item, location) {
      if (!stockValidationUrl) {
        return { available: true, unverified: true };
      }
      const response = await fetch(stockValidationUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          stock_id: item.stock_id,
          stock_hash: item.stock_hash,
          model: item.model,
          size: item.size,
          location
        })
      });
      if (!response.ok) throw new Error(t("stock_validation_failed"));
      const result = await response.json();
      if (result.available === false) throw new Error(t("stock_unavailable"));
      return result;
    }

    async function addStockToCart(item, location) {
      const selectedVariant = variantInfoForStock(item);
      const itemVariantId = selectedVariant?.id || (isClearanceStock(item) ? null : variantId);
      if (!itemVariantId || itemVariantId === "undefined") {
        throw new Error(t("stock_variant_missing", { size: item.size }));
      }
      const validation = await validateStockItem(item, location);
      if (!enableCartPosts) {
        return { previewOnly: true, validation };
      }
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: Number(itemVariantId),
          quantity: 1,
          properties: {
            "Order type": "PD stock canopy",
            "Stock reference": stockReferenceFor(item),
            "PD stock ID": String(item.stock_id),
            "Ships from": location,
            "Stock condition": item.category || "Standard Stock",
            "PD design URL": item.design_object_url || "",
            "_pd_stock_ref": String(item.stock_id),
            "_pd_stock_hash": item.stock_hash || "",
            "_pd_stock_model": item.model || "Navigator",
            "_pd_stock_size": item.size || "",
            "_pd_stock_category": item.category || "",
            "_pd_stock_line_set": item.details?.line_set || "",
            "_pd_stock_pattern": item.details?.pattern || "",
            "_pd_stock_view_url": item.view_url || "",
            "_pd_stock_design_object_url": item.design_object_url || "",
            "_pd_stock_validated": validation.unverified ? "not configured" : "true"
          }
        })
      });
      if (!response.ok) throw new Error(t("cart_rejected_stock"));
      const addedItem = await response.json();
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
      return addedItem;
    }

    function render() {
      const items = filteredItems();
      grid.innerHTML = "";
      if (count) {
        count.textContent = stockCountText(items.length);
      }
      if (!items.length) {
        grid.innerHTML = `<p class="pd-empty">${escapeHTML(t("no_stock_matches"))}</p>`;
        return;
      }
      items.forEach((item) => {
        const node = template.content.firstElementChild.cloneNode(true);
        node.querySelector("img").src = item.snapshot_url;
        node.querySelector("img").alt = t("stock_canopy_alt", { name: item.name });
        node.querySelector("h3").textContent = item.name;
        const badges = node.querySelector(".pd-badges");
        item.locations.forEach((location) => {
          const badge = document.createElement("span");
          badge.textContent = location;
          badges.append(badge);
        });
        if (item.category && !/standard/i.test(item.category)) {
          const badge = document.createElement("span");
          badge.className = "pd-badge--condition";
          badge.textContent = item.category.replace(/\s*stock$/i, "");
          badges.append(badge);
        }
        const price = node.querySelector("[data-stock-card-price]");
        const variantInfo = variantInfoForStock(item);
        const isClearance = isClearanceStock(item);
        const isManualReview = needsManualStockReview(item);
        const displayPrice = isManualReview ? usedPriceTextFor(item) : (variantInfo?.price || (isClearance ? "" : stockPrice));
        if (price && displayPrice) {
          price.hidden = false;
          price.querySelector("span").textContent = isManualReview ? usedPriceLabelFor(item) : (isClearance ? t("clearance_price") : t("stock_price"));
          price.querySelector("strong").textContent = displayPrice;
        }
        const dl = node.querySelector("dl");
        detailsFor(item).forEach(([label, value]) => {
          const row = document.createElement("div");
          row.className = `pd-stock-card__detail pd-stock-card__detail--${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
          row.innerHTML = `<dt>${escapeHTML(label === "Pattern" ? t("color_pattern") : label)}</dt><dd>${escapeHTML(value)}</dd>`;
          dl.append(row);
        });
        const stockButton = node.querySelector("[data-stock-add]");
        const reserveWhatsAppNumber = "34644271546";
        const reserveWhatsAppUrl = () => {
          const stockRef = item.stock_id ? ` (stock ID ${item.stock_id})` : "";
          const pageUrl = window.location.href.split("#")[0];
          return `https://wa.me/${reserveWhatsAppNumber}?text=${encodeURIComponent(t("reserve_whatsapp", { name: item.name, stock_ref: stockRef, page_url: pageUrl }))}`;
        };
        if (needsManualStockReview(item)) {
          stockButton.textContent = t("contact_reserve");
          stockButton.classList.add("pd-button--manual");
          stockButton.addEventListener("click", () => {
            window.location.href = reserveWhatsAppUrl();
          });
        } else {
          stockButton.addEventListener("click", async (event) => {
            const button = event.currentTarget;
            const selectedLocation = item.locations.includes(region) && region !== "all" ? region : item.locations[0];
            button.disabled = true;
            button.textContent = t("adding");
            try {
              const result = await addStockToCart(item, selectedLocation);
              button.textContent = result.previewOnly ? t("cart_preview_ready") : t("added_to_cart");
            } catch (error) {
              button.textContent = t("check_settings");
              console.error(error);
            } finally {
              setTimeout(() => {
                button.disabled = false;
                button.textContent = t("add_to_cart");
              }, 2400);
            }
          });
        }
        grid.append(node);
      });
    }

    filterButtons.forEach((button) => {
      button.addEventListener("click", () => {
        region = button.dataset.stockFilter;
        filterButtons.forEach((item) => item.classList.toggle("is-active", item === button));
        render();
      });
    });
    sizeOptions?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-stock-size]");
      if (!button) return;
      size = button.dataset.stockSize;
      sizeButtons().forEach((item) => item.classList.toggle("is-active", item === button));
      render();
    });

    hydrateVariantMapFromProductJson().finally(() => {
      renderFeatured();
      render();
    });
  }

  function initDesigner(section, templateData, colorData) {
    const translations = translationsFor(section);
    const t = (key, values) => translate(translations, key, values);
    const designer = section.querySelector(SELECTORS.designer);
    if (!designer || !templateData?.model) return;

    const model = templateData.model;
    const partsById = new Map(model.parts.map((part) => [part.id, part]));
    const canvas = designer.querySelector("[data-designer-canvas]");
    const loading = designer.querySelector("[data-designer-loading]");
    const swatches = designer.querySelector("[data-color-swatches]");
    const modes = designer.querySelector("[data-color-modes]");
    const status = designer.querySelector("[data-designer-status]");
    const currentColor = designer.querySelector("[data-current-color]");
    const sizeSelect = designer.querySelector("[data-design-size]");
    const snapshotInput = designer.querySelector("[data-design-snapshot]");
    const copyLinkButton = designer.querySelector("[data-designer-copy-link]");
    const whatsAppButton = designer.querySelector("[data-designer-whatsapp]");
    const shareButton = designer.querySelector("[data-designer-share]");
    const shareActions = designer.querySelector("[data-share-actions]");
    const variantId = section.dataset.customVariantId;
    const enableCartPosts = section.dataset.enableCartPosts === "true";
    const designSaveUrl = section.dataset.designSaveUrl;
    const assetBase = section.dataset.assetProbe.replace(/pd-navigator-designer(?:-v\d+)?\.js(?:\?.*)?$/, "");
    const sizeVariantMap = (() => {
      try { return JSON.parse(section.dataset.sizeVariantMap || "{}"); } catch { return {}; }
    })();
    const defaultModeSet = model.colorPartGroupSets.find((set) => set.name === "Match Cell");
    const defaultModeGroup = defaultModeSet ? null : model.colorPartGroups.find((group) => group.name === "Whole Canopy");

    function variantInfoForSize(sizeValue) {
      return sizeVariantMap[String(sizeValue)] || sizeVariantMap[String(sizeValue).replace(/\s*sq\s*ft$/i, "")] || null;
    }

    const state = {
      selectedColor: DEFAULT_COLOR,
      selectedMode: defaultModeSet ? { type: "set", setId: defaultModeSet.id } : (defaultModeGroup ? { type: "group", groupId: defaultModeGroup.id } : { type: "individual" }),
      expanded: false,
      yaw: -0.8,
      pitch: -0.1,
      zoom: DEFAULT_VIEW_ZOOM,
      defaultZoom: DEFAULT_VIEW_ZOOM,
      partColors: new Map(model.parts.map((part) => [part.id, part.color || DEFAULT_COLOR])),
      meshes: new Map(),
      ready: false,
      pointer: null,
      coloredPartIds: new Set(model.parts.filter((part) => !part.hasFixedColor).map((part) => part.id)),
      loadedFromShare: false,
      designName: t(DEFAULT_DESIGN_NAME_KEY),
      currentShare: null
    };

    let renderer;
    let scene;
    let camera;
    let rootGroup;
    let raycaster;
    let pointerVector;

    function setStatus(message, tone = "info") {
      if (!status) return;
      const visibleMessage = tone === "error" || tone === "success" ? (message || "") : "";
      status.textContent = visibleMessage;
      status.hidden = !visibleMessage;
      status.classList.toggle("is-error", tone === "error");
      status.classList.toggle("is-success", tone === "success");
    }

    const SHORTENER_ENDPOINT = "https://tfx-photo-upload.young-sky-a2c1.workers.dev";

    function whatsappShareUrl(url) {
      return `https://wa.me/?text=${encodeURIComponent(t("whatsapp_design", { url }))}`;
    }

    async function shortenShareUrl(url, prefix = "pd-navigator") {
      try {
        const response = await fetch(`${SHORTENER_ENDPOINT}/shorten`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url, prefix })
        });
        if (!response.ok) return url;
        const result = await response.json();
        return result.shortURL || result.shortUrl || result.url || url;
      } catch {
        return url;
      }
    }

    function setShareLink(url) {
      if (shareActions) shareActions.hidden = !url;
      if (shareButton && !shareButton.disabled) shareButton.textContent = t("save_design");
      if (copyLinkButton) {
        copyLinkButton.disabled = !url;
        if (!url) {
          copyLinkButton.textContent = t("copy_design_link");
          copyLinkButton.classList.remove("pd-share-action--success");
        }
      }
      if (whatsAppButton) {
        whatsAppButton.setAttribute("aria-disabled", url ? "false" : "true");
        if (url) {
          whatsAppButton.setAttribute("href", whatsappShareUrl(url));
        } else {
          whatsAppButton.removeAttribute("href");
        }
      }
    }

    function clearShareLink() {
      state.currentShare = null;
      setShareLink("");
    }

    function colorName(hex) {
      return colorData?.colors?.[compactHex(hex)] || cleanHex(hex);
    }

    function meshUrl(part) {
      return `${assetBase}${encodeURIComponent(part.fileName)}`;
    }

    function keepsPreviewMeshOpaque(part) {
      const label = `${part?.id || ""} ${part?.name || ""} ${part?.fileName || ""}`.toLowerCase();
      return label.includes("tailribs") || label.includes("tail ribs") || label.includes("slider");
    }

    function buildSwatches() {
      const palette = model.colorPalettes.find((item) => item.id === model.colorPaletteId) || model.colorPalettes[0];
      swatches.innerHTML = "";
      palette.colorPaletteColorCodes.forEach((hex, index) => {
        const normalized = cleanHex(hex);
        const button = document.createElement("button");
        button.type = "button";
        button.style.setProperty("--swatch", normalized);
        button.title = colorName(normalized);
        button.setAttribute("aria-label", colorName(normalized));
        button.classList.toggle("is-active", index === 0);
        button.addEventListener("click", () => {
          state.selectedColor = normalized;
          [...swatches.children].forEach((item) => item.classList.toggle("is-active", item === button));
          updateCurrentColor();
        });
        swatches.append(button);
      });
      state.selectedColor = cleanHex(palette.colorPaletteColorCodes[0]);
      updateCurrentColor();
    }

    function updateCurrentColor() {
      if (currentColor) currentColor.textContent = t("selected_color", { color: colorName(state.selectedColor) });
    }

    function buttonMode(button, mode) {
      button.addEventListener("click", () => {
        state.selectedMode = mode;
        [...modes.children].forEach((item) => item.classList.toggle("is-active", item === button));
        setStatus(t("color_mode", { mode: button.textContent }));
      });
    }

    function displayModeName(name) {
      return String(name || "")
        .replace(/^Tail Ribs \((All Cells|All the same Color)\)$/i, "Tail Ribs (All)")
        .replace(/^Symmetrical (Color )?Pattern$/i, "Symmetrical Pattern");
    }

    function buildModes() {
      modes.innerHTML = "";
      const individual = document.createElement("button");
      individual.type = "button";
      individual.textContent = t("individual_parts");
      individual.classList.toggle("is-active", !defaultModeSet && !defaultModeGroup);
      buttonMode(individual, { type: "individual" });
      modes.append(individual);

      model.colorPartGroups.forEach((group) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = displayModeName(group.name);
        button.classList.toggle("is-active", defaultModeGroup?.id === group.id);
        buttonMode(button, { type: "group", groupId: group.id });
        modes.append(button);
      });

      model.colorPartGroupSets.forEach((set) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = displayModeName(set.name);
        button.classList.toggle("is-active", defaultModeSet?.id === set.id);
        buttonMode(button, { type: "set", setId: set.id });
        modes.append(button);
      });
    }

    function targetPartIds(clickedPartId) {
      if (state.selectedMode.type === "individual") return [clickedPartId];
      if (state.selectedMode.type === "group") {
        const group = model.colorPartGroups.find((item) => item.id === state.selectedMode.groupId);
        return group?.partIds || [clickedPartId];
      }
      if (state.selectedMode.type === "set") {
        const set = model.colorPartGroupSets.find((item) => item.id === state.selectedMode.setId);
        const group = set?.groups.find((item) => item.partIds.includes(clickedPartId));
        return group?.partIds || [clickedPartId];
      }
      return [clickedPartId];
    }

    function applyColor(clickedPartId) {
      targetPartIds(clickedPartId).forEach((partId) => {
        const mesh = state.meshes.get(partId);
        const part = partsById.get(partId);
        if (!mesh || part?.hasFixedColor) return;
        mesh.material.color.set(state.selectedColor);
        state.partColors.set(partId, state.selectedColor);
        state.coloredPartIds.add(partId);
      });
      clearShareLink();
      setStatus(t("color_applied", { color: colorName(state.selectedColor) }));
    }

    function preferredViewZoom() {
      const width = canvas.parentElement?.getBoundingClientRect().width || 0;
      return width > 0 && width < COMPACT_VIEW_MAX_WIDTH ? COMPACT_VIEW_ZOOM : DEFAULT_VIEW_ZOOM;
    }

    function syncResponsiveZoom() {
      const nextDefaultZoom = preferredViewZoom();
      if (state.zoom === state.defaultZoom) {
        state.zoom = nextDefaultZoom;
      }
      state.defaultZoom = nextDefaultZoom;
    }

    function resizeRenderer() {
      if (!renderer || !camera) return;
      const box = canvas.parentElement.getBoundingClientRect();
      const width = Math.max(320, Math.floor(box.width));
      const height = Math.max(320, Math.floor(box.height));
      renderer.setSize(width, height, false);
      syncResponsiveZoom();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }

    function updateModelTransform() {
      if (!rootGroup) return;
      rootGroup.rotation.y = state.yaw;
      rootGroup.rotation.x = state.pitch;
      state.meshes.forEach((mesh, partId) => {
        const part = partsById.get(partId);
        mesh.position.y = state.expanded ? part.expandLevel * 3.8 : 0;
      });
      if (camera) camera.position.z = state.zoom;
    }

    function renderLoop() {
      if (!renderer || !scene || !camera) return;
      updateModelTransform();
      renderer.render(scene, camera);
      window.requestAnimationFrame(renderLoop);
    }

    function parseLegacyGeometry(json) {
      const geometry = new THREE.Geometry();
      const vertices = json.vertices || [];
      const faces = json.faces || [];
      for (let index = 0; index < vertices.length; index += 3) {
        geometry.vertices.push(new THREE.Vector3(vertices[index], vertices[index + 1], vertices[index + 2]));
      }

      let offset = 0;
      while (offset < faces.length) {
        const type = faces[offset++];
        const isQuad = (type & 1) === 1;
        const hasMaterial = (type & 2) === 2;
        const hasFaceUv = (type & 4) === 4;
        const hasFaceVertexUv = (type & 8) === 8;
        const hasFaceNormal = (type & 16) === 16;
        const hasFaceVertexNormal = (type & 32) === 32;
        const hasFaceColor = (type & 64) === 64;
        const hasFaceVertexColor = (type & 128) === 128;
        const vertexCount = isQuad ? 4 : 3;
        const faceVertices = faces.slice(offset, offset + vertexCount);
        offset += vertexCount;
        if (hasMaterial) offset += 1;
        if (hasFaceUv) offset += 1;
        if (hasFaceVertexUv) offset += vertexCount;
        if (hasFaceNormal) offset += 1;
        if (hasFaceVertexNormal) offset += vertexCount;
        if (hasFaceColor) offset += 1;
        if (hasFaceVertexColor) offset += vertexCount;

        if (isQuad) {
          geometry.faces.push(new THREE.Face3(faceVertices[0], faceVertices[1], faceVertices[2]));
          geometry.faces.push(new THREE.Face3(faceVertices[0], faceVertices[2], faceVertices[3]));
        } else {
          geometry.faces.push(new THREE.Face3(faceVertices[0], faceVertices[1], faceVertices[2]));
        }
      }
      geometry.computeFaceNormals();
      return geometry;
    }

    async function loadPart(part) {
      const response = await fetch(meshUrl(part), { credentials: "same-origin" });
      if (!response.ok) throw new Error(t("mesh_load_failed", { file_name: part.fileName }));
      const json = await response.json();
      const geometry = parseLegacyGeometry(json);
      geometry.computeBoundingBox();
      geometry.computeVertexNormals();
      const shouldStayOpaque = keepsPreviewMeshOpaque(part);
      const material = new THREE.MeshPhongMaterial({
        color: state.partColors.get(part.id) || DEFAULT_COLOR,
        shininess: 18,
        side: THREE.DoubleSide,
        transparent: !shouldStayOpaque,
        opacity: shouldStayOpaque ? 1 : PREVIEW_MESH_OPACITY
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData.partId = part.id;
      mesh.userData.partName = part.name;
      state.meshes.set(part.id, mesh);
      rootGroup.add(mesh);
      return mesh;
    }

    function centerModel() {
      const box = new THREE.Box3().setFromObject(rootGroup);
      const center = new THREE.Vector3();
      box.getCenter(center);
      rootGroup.children.forEach((mesh) => {
        mesh.position.x -= center.x;
        mesh.position.y -= center.y;
        mesh.position.z -= center.z;
      });
    }

    async function initThree() {
      if (!window.THREE) {
        throw new Error(t("three_missing"));
      }
      if (!THREE.Geometry || !THREE.Face3) {
        throw new Error(t("three_geometry_missing"));
      }

      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(38, 1, 0.1, 1000);
      camera.position.set(0, 0, state.zoom);
      rootGroup = new THREE.Group();
      scene.add(rootGroup);
      raycaster = new THREE.Raycaster();
      pointerVector = new THREE.Vector2();

      [
        [0, 15, 20, 0.9],
        [20, 12, 10, 0.55],
        [-18, -10, 15, 0.45],
        [0, -20, -20, 0.35]
      ].forEach(([x, y, z, intensity]) => {
        const light = new THREE.DirectionalLight(0xffffff, intensity);
        light.position.set(x, y, z);
        scene.add(light);
      });
      scene.add(new THREE.AmbientLight(0xffffff, 0.35));

      let loaded = 0;
      for (const part of model.parts) {
        if (loading) loading.textContent = t("loading_part", { current: loaded + 1, total: model.parts.length });
        await loadPart(part);
        loaded += 1;
      }
      centerModel();
      resizeRenderer();
      state.ready = true;
      if (loading) loading.hidden = true;
      if (state.loadedFromShare) setStatus(t("shared_design_loaded"));
      renderLoop();
    }

    function pickPart(event) {
      if (!state.ready) return;
      const rect = canvas.getBoundingClientRect();
      pointerVector.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointerVector.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointerVector, camera);
      const intersections = raycaster.intersectObjects([...state.meshes.values()], false);
      if (intersections[0]) {
        applyColor(intersections[0].object.userData.partId);
      }
    }

    function bindPointer() {
      canvas.addEventListener("pointerdown", (event) => {
        canvas.setPointerCapture(event.pointerId);
        state.pointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          yaw: state.yaw,
          pitch: state.pitch,
          moved: 0
        };
      });
      canvas.addEventListener("pointermove", (event) => {
        if (!state.pointer || state.pointer.id !== event.pointerId) return;
        const dx = event.clientX - state.pointer.x;
        const dy = event.clientY - state.pointer.y;
        state.pointer.moved = Math.max(state.pointer.moved, Math.abs(dx) + Math.abs(dy));
        state.yaw = state.pointer.yaw + dx * 0.01;
        state.pitch = Math.max(-1.1, Math.min(0.9, state.pointer.pitch + dy * 0.008));
      });
      canvas.addEventListener("pointerup", (event) => {
        if (!state.pointer || state.pointer.id !== event.pointerId) return;
        const moved = state.pointer.moved;
        state.pointer = null;
        if (moved < 8) pickPart(event);
      });
      canvas.addEventListener("wheel", (event) => {
        event.preventDefault();
        state.zoom = Math.max(MIN_VIEW_ZOOM, Math.min(MAX_VIEW_ZOOM, state.zoom + Math.sign(event.deltaY) * 2));
      }, { passive: false });
    }

    function snapshot() {
      if (!renderer || !scene || !camera) return "";
      const priorYaw = state.yaw;
      const priorPitch = state.pitch;
      const priorZoom = state.zoom;
      state.yaw = -0.8;
      state.pitch = -0.1;
      state.zoom = 24;
      updateModelTransform();
      renderer.render(scene, camera);
      const dataUrl = renderer.domElement.toDataURL("image/png");
      state.yaw = priorYaw;
      state.pitch = priorPitch;
      state.zoom = priorZoom;
      if (snapshotInput) snapshotInput.value = dataUrl;
      return dataUrl;
    }

    function uncoloredParts() {
      return model.parts.filter((part) => !part.hasFixedColor && !state.coloredPartIds.has(part.id));
    }

    function missingPartSummary(parts) {
      const groups = model.colorPartGroups
        .filter((group) => group.name !== "Whole Canopy")
        .map((group) => {
          const missingIds = parts.filter((part) => group.partIds.includes(part.id)).map((part) => part.id);
          return missingIds.length ? { name: group.name, count: missingIds.length, ids: missingIds } : null;
        })
        .filter(Boolean);
      const coveredIds = new Set(groups.flatMap((group) => group.ids));
      const ungrouped = parts.filter((part) => !coveredIds.has(part.id));
      return [
        ...groups.map((group) => `${group.name} (${group.count})`),
        ...ungrouped.slice(0, 6).map((part) => part.name)
      ].join(", ");
    }

    function assertCompleteDesign(actionLabel) {
      const missing = uncoloredParts();
      if (!missing.length) return;
      const noun = missing.length === 1 ? t("part_needs") : t("parts_need");
      const summary = missingPartSummary(missing);
      const message = t("full_canopy_required", { action: actionLabel, count: missing.length, noun, summary: summary ? `: ${summary}` : "" });
      setStatus(message, "error");
      status?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      const error = new Error(message);
      error.name = "ValidationError";
      throw error;
    }

    function designPayload(options = {}) {
      const includeSnapshot = options.includeSnapshot === true;
      const partColors = model.parts.map((part) => ({
        partId: part.id,
        partName: part.name,
        color: state.partColors.get(part.id) || DEFAULT_COLOR
      }));
      return {
        id: `pd-navigator-${Date.now()}`,
        modelId: model.canopyModelId,
        modelName: model.name,
        sizeId: sizeSelect.value,
        size: sizeSelect.options[sizeSelect.selectedIndex]?.textContent || "",
        name: state.designName,
        savedAt: new Date().toISOString(),
        partColors,
        snapshot: includeSnapshot ? snapshot() : ""
      };
    }

    function partColorString(payload) {
      return payload.partColors.map((item) => `${item.partId}:${compactHex(item.color)}`).join(";");
    }

    function applyPartColorString(value) {
      String(value || "").split(";").forEach((pair) => {
        const [id, color] = pair.split(":");
        const partId = Number(id);
        if (!partsById.has(partId) || !/^(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color || "")) return;
        const normalized = cleanHex(color);
        state.partColors.set(partId, normalized);
        state.coloredPartIds.add(partId);
        const mesh = state.meshes.get(partId);
        if (mesh) mesh.material.color.set(normalized);
      });
    }

    function compactShareState(payload) {
      return {
        v: 1,
        m: payload.modelId,
        s: payload.sizeId,
        n: payload.name,
        c: partColorString(payload)
      };
    }

    function fallbackShareUrl(payload) {
      const url = new URL(window.location.href);
      url.searchParams.set(SHARE_PARAM, encodeBase64Url(JSON.stringify(compactShareState(payload))));
      url.hash = "designer";
      return url.toString();
    }

    function loadSharedDesignFromUrl() {
      const token = new URL(window.location.href).searchParams.get(SHARE_PARAM);
      if (!token) return false;
      try {
        const shared = JSON.parse(decodeBase64Url(token));
        if (shared.v !== 1 || Number(shared.m) !== Number(model.canopyModelId) || !shared.c) {
          throw new Error(t("bad_shared_link"));
        }
        if (shared.n) state.designName = shared.n;
        if (shared.s && [...sizeSelect.options].some((option) => option.value === String(shared.s))) {
          sizeSelect.value = String(shared.s);
        }
        applyPartColorString(shared.c);
        state.loadedFromShare = true;
        setShareLink(window.location.href);
        return true;
      } catch (error) {
        console.error(error);
        setStatus(t("shared_link_failed"));
        return false;
      }
    }

    function fallbackHash(value) {
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return Math.abs(hash).toString(16).padStart(8, "0").repeat(3).slice(0, 24);
    }

    async function sha256(value) {
      if (!window.crypto?.subtle) return fallbackHash(value);
      const bytes = new TextEncoder().encode(value);
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    async function localPersistedDesign(payload) {
      const compactColors = partColorString(payload);
      const designHash = (await sha256(`${payload.modelId}|${payload.sizeId}|${payload.name}|${compactColors}`)).slice(0, 24);
      return {
        design_ref: `pd-navigator-${designHash.slice(0, 12)}`,
        design_hash: designHash,
        preview_url: "",
        local_only: true
      };
    }

    async function persistDesign(payload, shareUrl) {
      const compactColors = partColorString(payload);
      if (!designSaveUrl) {
        return localPersistedDesign(payload);
      }
      const response = await fetch(designSaveUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          model_id: payload.modelId,
          model: payload.modelName,
          size_id: payload.sizeId,
          size: payload.size,
          design_name: payload.name,
          part_colors: compactColors,
          snapshot: payload.snapshot,
          fallback_share_url: shareUrl
        })
      });
      if (!response.ok) throw new Error(t("design_save_failed"));
      const result = await response.json();
      const local = await localPersistedDesign(payload);
      return {
        ...result,
        design_ref: result.design_ref || local.design_ref,
        design_hash: result.design_hash || local.design_hash,
        preview_url: result.preview_url || "",
        share_url: result.share_url || result.shareUrl || result.design_url || result.url || ""
      };
    }

    async function createShareLink(options = {}) {
      assertCompleteDesign(t("share_action"));
      const payload = designPayload({ includeSnapshot: Boolean(designSaveUrl) });
      const fallbackUrl = fallbackShareUrl(payload);
      if (state.currentShare?.signature === fallbackUrl) return state.currentShare;

      let persisted;
      let shareUrl = fallbackUrl;
      let backendUnavailable = false;
      try {
        persisted = await persistDesign(payload, fallbackUrl);
        const persistedUrl = persisted.share_url || persisted.shareUrl || persisted.design_url || persisted.url || fallbackUrl;
        shareUrl = await shortenShareUrl(persistedUrl);
      } catch (error) {
        console.error(error);
        backendUnavailable = true;
        persisted = await localPersistedDesign(payload);
      }

      state.currentShare = {
        signature: fallbackUrl,
        url: shareUrl,
        fallbackUrl,
        persisted,
        payload
      };
      setShareLink(shareUrl);
      return state.currentShare;
    }

    function fallbackCopy(value) {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-999px";
      document.body.append(textarea);
      textarea.select();
      document.execCommand("copy");
      textarea.remove();
    }

    async function copyShareLink() {
      const share = state.currentShare || await createShareLink({ announce: false });
      try {
        if (!navigator.clipboard?.writeText) throw new Error(t("clipboard_unavailable"));
        await navigator.clipboard.writeText(share.url);
      } catch {
        fallbackCopy(share.url);
      }
      setStatus(t("design_link_copied"));
    }

    async function shareToWhatsApp() {
      const share = state.currentShare || await createShareLink({ announce: false });
      window.open(whatsappShareUrl(share.url), "_blank", "noopener");
      setStatus(t("whatsapp_opening"));
    }

    async function addCustomToCart() {
      const share = await createShareLink({ announce: false });
      const { payload, persisted } = share;
      const selectedVariant = variantInfoForSize(payload.sizeId) || variantInfoForSize(payload.size);
      const itemVariantId = selectedVariant?.id || variantId;
      if (!itemVariantId || itemVariantId === "undefined") {
        throw new Error(t("stock_variant_missing", { size: payload.size || payload.sizeId }));
      }
      if (!enableCartPosts) {
        return { previewOnly: true };
      }
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          id: Number(itemVariantId),
          quantity: 1,
          properties: {
            "Order type": "Custom PD Navigator design",
            Model: payload.modelName,
            Size: payload.size,
            "Design link": share.url,
            "_pd_design_ref": persisted.design_ref,
            "_pd_design_hash": persisted.design_hash,
            "_pd_model_id": String(payload.modelId),
            "_pd_size_id": String(payload.sizeId),
            "_pd_design_name": payload.name,
            "_pd_design_preview": persisted.preview_url || "",
            "_pd_color_summary": `${payload.partColors.length} Navigator parts configured`
          }
        })
      });
      if (!response.ok) throw new Error(t("cart_rejected_custom"));
      const item = await response.json();
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:refresh"));
      document.dispatchEvent(new CustomEvent("dispatch:cart-drawer:open"));
      return item;
    }

    designer.querySelector("[data-designer-reset]")?.addEventListener("click", () => {
      state.partColors = new Map(model.parts.map((part) => [part.id, part.color || DEFAULT_COLOR]));
      state.coloredPartIds = new Set(model.parts.filter((part) => !part.hasFixedColor).map((part) => part.id));
      state.meshes.forEach((mesh, partId) => mesh.material.color.set(state.partColors.get(partId)));
      clearShareLink();
      setStatus(t("colors_reset"));
    });

    shareButton?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      button.disabled = true;
      button.textContent = t("saving");
      try {
        await createShareLink();
      } catch (error) {
        if (error.name !== "ValidationError") console.error(error);
        setStatus(error.message, "error");
      } finally {
        button.disabled = false;
        button.textContent = t("save_design");
      }
    });

    copyLinkButton?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      try {
        await copyShareLink();
        button.textContent = t("copied");
        button.classList.add("pd-share-action--success");
        window.setTimeout(() => {
          button.textContent = t("copy_design_link");
          button.classList.remove("pd-share-action--success");
        }, 2000);
      } catch (error) {
        console.error(error);
        setStatus(t("copy_failed"));
      }
    });

    whatsAppButton?.addEventListener("click", async (event) => {
      event.preventDefault();
      try {
        await shareToWhatsApp();
      } catch (error) {
        if (error.name !== "ValidationError") console.error(error);
        setStatus(error.message, "error");
      }
    });

    designer.querySelector("[data-designer-add]")?.addEventListener("click", async (event) => {
      const button = event.currentTarget;
      button.disabled = true;
      button.textContent = t("adding");
      try {
        const result = await addCustomToCart();
        button.textContent = result.previewOnly ? t("cart_preview_ready") : t("added_to_cart");
        setStatus(result.previewOnly ? t("design_link_ready") : t("design_link_added"));
      } catch (error) {
        if (error.name !== "ValidationError") console.error(error);
        setStatus(error.message, "error");
        button.textContent = t("check_settings");
      } finally {
        setTimeout(() => {
          button.disabled = false;
          button.textContent = t("add_custom");
        }, 2600);
      }
    });

    designer.querySelectorAll("[data-view]").forEach((button) => {
      button.addEventListener("click", () => {
        const action = button.dataset.view;
        if (action === "zoom-in") state.zoom = Math.max(MIN_VIEW_ZOOM, state.zoom - 3);
        if (action === "zoom-out") state.zoom = Math.min(MAX_VIEW_ZOOM, state.zoom + 3);
        if (action === "expand") {
          state.expanded = !state.expanded;
          button.textContent = state.expanded ? "⤡" : "⤢";
          button.title = state.expanded ? t("contract_canopy") : t("expand_canopy");
          button.setAttribute("aria-label", state.expanded ? t("contract_canopy") : t("expand_canopy"));
        }
        if (action === "reset-view") {
          state.yaw = -0.8;
          state.pitch = -0.1;
          state.zoom = preferredViewZoom();
          state.defaultZoom = state.zoom;
        }
      });
    });

    window.addEventListener("resize", resizeRenderer);
    sizeSelect?.addEventListener("change", clearShareLink);
    buildSwatches();
    buildModes();
    bindPointer();
    loadSharedDesignFromUrl();
    initThree().catch((error) => {
      console.error(error);
      if (loading) loading.textContent = error.message;
      setStatus(t("designer_load_failed"));
    });
  }

  function scrollToActiveHash() {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    window.requestAnimationFrame(() => target.scrollIntoView({ block: "start" }));
  }

  async function initSection(section) {
    const translations = translationsFor(section);
    const t = (key, values) => translate(translations, key, values);
    initTabs(section);
    initHeroGallery(section);
    try {
      const liveStockUrl = section.dataset.liveStockUrl;
      const staticStockUrl = section.dataset.stockUrl;
      function liveStockUrlForMarket(url) {
        if (!url) return url;
        try {
          const next = new URL(url, window.location.origin);
          const country = (section.dataset.customerCountry || "").toUpperCase();
          const currency = section.dataset.moneyCurrency || "";
          const market = section.dataset.marketHandle || "";
          const marketName = section.dataset.marketName || "";
          if (country && !next.searchParams.get("country")) next.searchParams.set("country", country);
          if (currency && !next.searchParams.get("currency")) next.searchParams.set("currency", currency);
          if (market && !next.searchParams.get("market")) next.searchParams.set("market", market);
          if (marketName && !next.searchParams.get("market_name")) next.searchParams.set("market_name", marketName);
          return next.toString();
        } catch {
          return url;
        }
      }
      async function loadStockData() {
        if (liveStockUrl) {
          try {
            const liveStock = await getJson(liveStockUrlForMarket(liveStockUrl), t);
            if (Array.isArray(liveStock?.items) && liveStock.items.length > 0) return liveStock;
          } catch {}
        }
        return getJson(staticStockUrl, t);
      }
      const [stockData, templateData, colorData] = await Promise.all([
        loadStockData(),
        getJson(section.dataset.templateUrl, t),
        getJson(section.dataset.colorsUrl, t)
      ]);
      initStock(section, stockData);
      initDesigner(section, templateData, colorData);
      scrollToActiveHash();
    } catch (error) {
      console.error(error);
      const grid = section.querySelector(SELECTORS.stockGrid);
      if (grid) grid.innerHTML = `<p class="pd-error">${escapeHTML(error.message)}</p>`;
    }
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);
  });
})();
